import { useState, useEffect, useCallback, useRef } from 'react';
import { Measure, SavedChartState, AudioSettings, TimeSignature, KeySignature, UISettings, RecordedMidiNote } from '../types';
import * as db from '../utils/db';
import * as drive from '../utils/drive';
import { CURRENT_CHART_SCHEMA_VERSION } from '../utils/chartMigrations';

export interface ChartStateForAutoSave {
  songTitle: string;
  songAuthor: string;
  songStyle: string;
  numberOfMeasures: number;
  timeSignature: TimeSignature;
  keySignature: KeySignature;
  sourceChart: Measure[];
  transposeAmount: number;
  audioSettings: AudioSettings;
  uiSettings: UISettings;
  recordedNotes?: RecordedMidiNote[];
}

export interface UseAutoSaveReturn {
  changeCount: number;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  saveStatus: 'idle' | 'saving' | 'saved';
  setSaveStatus: React.Dispatch<React.SetStateAction<'idle' | 'saving' | 'saved'>>;
  saveStatusTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  markSaved: () => void;
}

export function useAutoSave(
  chartState: ChartStateForAutoSave,
  userProfile: { email?: string } | null,
  onBeforeUnloadExtra?: () => void,
): UseAutoSaveReturn {
  const {
    songTitle, songAuthor, songStyle, numberOfMeasures,
    timeSignature, keySignature, sourceChart,
    transposeAmount, audioSettings, uiSettings,
    recordedNotes,
  } = chartState;

  // Auto-save change tracking
  const [changeCount, setChangeCount] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Save status for visual feedback
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track last chart state to prevent double counting
  const lastChartRef = useRef<Measure[]>([]);
  // Track last recordedNotes state (MiniDaw) to prevent double counting
  const lastRecordedNotesRef = useRef<RecordedMidiNote[]>([]);

  // Mark as saved (used by handleSave in App.tsx)
  const markSaved = useCallback(() => {
    setChangeCount(0);
    setHasUnsavedChanges(false);
  }, []);

  // Auto-save mechanism
  const performAutoSave = useCallback(async () => {
    if (!songTitle.trim()) return;

    setSaveStatus('saving');

    try {
      const stateToSave: SavedChartState = {
        chartSchemaVersion: CURRENT_CHART_SCHEMA_VERSION,
        songTitle,
        songAuthor,
        songStyle,
        numberOfMeasures,
        timeSignature,
        keySignature,
        sourceChart,
        transposeAmount,
        audioSettings,
        uiSettings,
        recordedNotes,
        lastModified: new Date().toISOString(),
      };

      await db.saveChart(stateToSave);
      setChangeCount(0);
      setHasUnsavedChanges(false);

      // Show "saved" status briefly
      setSaveStatus('saved');
      if (saveStatusTimerRef.current) {
        clearTimeout(saveStatusTimerRef.current);
      }
      saveStatusTimerRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);

      // Clear timer after successful save
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    } catch (error) {
      console.error("Auto-save failed:", error);
      setSaveStatus('idle');
    }
  }, [songTitle, songAuthor, songStyle, numberOfMeasures, timeSignature, keySignature, sourceChart, transposeAmount, audioSettings, uiSettings, recordedNotes]);

  // Simple change tracking - only track sourceChart changes (main user edits)
  useEffect(() => {
    if (!songTitle.trim()) return;

    // Check if chart actually changed by comparing with last known state
    const chartChanged = JSON.stringify(sourceChart) !== JSON.stringify(lastChartRef.current);
    if (!chartChanged) return;

    // Update the ref with current chart
    lastChartRef.current = sourceChart;

    // Mark that we have unsaved changes
    setHasUnsavedChanges(true);

    // Increment change count
    setChangeCount(prev => {
      const newCount = prev + 1;

      // Auto-save immediately if we reach 8 changes
      if (newCount >= 8) {
        console.log('Auto-saving after 8 changes');
        performAutoSave();
        return 0; // Reset count after save
      }

      return newCount;

  // Change tracking for MiniDaw recordings - separate from sourceChart so a piano
  // recording alone (no grid edits) still triggers autosave / quit-save.
  useEffect(() => {
    if (!songTitle.trim()) return;

    const notesChanged = JSON.stringify(recordedNotes || []) !== JSON.stringify(lastRecordedNotesRef.current);
    if (!notesChanged) return;

    lastRecordedNotesRef.current = recordedNotes || [];
    setHasUnsavedChanges(true);

    setChangeCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 8) {
        console.log('Auto-saving after 8 changes (recordedNotes)');
        performAutoSave();
        return 0;
      }
      return newCount;
    });
  }, [recordedNotes, performAutoSave]);
    });
  }, [sourceChart, performAutoSave]);

  // Separate 60-second timer management
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    console.log('Setting 60-second auto-save timer');

    // Clear any existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new 60-second timer
    autoSaveTimerRef.current = setTimeout(() => {
      console.log('Triggering auto-save due to 60-second timer');
      performAutoSave();
    }, 60000);

    // Cleanup function
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [hasUnsavedChanges, performAutoSave]);

  // Quit-time auto-save - save when user leaves the app
  useEffect(() => {
    const performQuitSave = async () => {
      if (!hasUnsavedChanges || !songTitle.trim()) return;

      console.log('Performing quit-time auto-save...');

      try {
        const stateToSave: SavedChartState = {
          chartSchemaVersion: CURRENT_CHART_SCHEMA_VERSION,
          songTitle,
          songAuthor,
          numberOfMeasures,
          timeSignature,
          keySignature,
          sourceChart,
          transposeAmount,
          audioSettings,
          uiSettings,
          recordedNotes,
          lastModified: new Date().toISOString(),
        };

        // Save locally first (quick)
        await db.saveChart(stateToSave);
        await db.setLastOpenedChartTitle(songTitle);
        console.log('Quit-time local save completed');

        // Also try cloud save if user is authenticated
        if (userProfile) {
          try {
            await drive.saveChartToDrive(stateToSave);
            console.log('Quit-time cloud save completed');
          } catch (error) {
            console.error('Quit-time cloud save failed:', error);
          }
        }

        // Reset change tracking
        setHasUnsavedChanges(false);
        setChangeCount(0);
      } catch (error) {
        console.error('Quit-time auto-save failed:', error);
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Allow navigation during Stripe checkout redirect
      if ((window as any).ALLOW_CHECKOUT_NAVIGATION) {
        return;
      }

      // Extra cleanup (e.g., leave jam session)
      onBeforeUnloadExtra?.();

      if (hasUnsavedChanges) {
        // Perform synchronous save attempt
        performQuitSave();

        // Show browser warning for unsaved changes
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && hasUnsavedChanges) {
        // User switched tabs or minimized - save changes
        performQuitSave();
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasUnsavedChanges, changeCount, songTitle, songAuthor, songStyle, sourceChart, numberOfMeasures, timeSignature, keySignature, transposeAmount, audioSettings, uiSettings, userProfile, recordedNotes]);

  return {
    changeCount,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    saveStatus,
    setSaveStatus,
    saveStatusTimerRef,
    markSaved,
  };
}
